import { SerializedFile, SerializedWidget } from 'fym-shared/src/smartlinks/types';
import { SmartLinkWidgetType } from 'fym-shared/src/smartlinks/enums';
import { MusicService } from 'fym-shared/src/music-services/types';
import { musicServicesByLinkType, musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { widgetPictureUrl } from 'fym-shared/src/smartlinks/widgetPicture';

/** The slice of a page version these facts are read from. */
export interface PublishedVersionFacts {
  widgets: SerializedWidget[] | null;
  backgroundFile: SerializedFile | null;
}

/**
 * The music services the page routes to, in the order fans see them.
 *
 * Read off the page's own link blocks rather than off the release it was made
 * from: a block removed by hand is a service the page no longer offers, and the
 * sheet must say what the page does, not what it was built from.
 */
export function linkedMusicServices(version: PublishedVersionFacts | null | undefined): MusicService[] {
  const widgets = version?.widgets ?? [];
  const services: MusicService[] = [];
  [...widgets]
    .sort((a, b) => a.position - b.position)
    .forEach((widget) => {
      if (widget.type !== SmartLinkWidgetType.link || !widget.data.isMusicService) {
        return;
      }
      const service = musicServicesByLinkType[widget.data.type];
      if (service !== undefined && !services.includes(service)) {
        services.push(service);
      }
    });
  return services;
}

/**
 * The big services a release page is expected to carry and does not.
 *
 * The list is the one the pre-save block offers, which is the app's own answer
 * to "which services matter" — not a second opinion invented here.
 */
export function missingMajorServices(linked: MusicService[]): MusicService[] {
  return defaultPresaveServices
    .map((integrationId) => musicServicesByMusicAPIID[integrationId])
    .filter((service): service is MusicService => service !== undefined && !linked.includes(service));
}

/**
 * The page's own artwork: the first cover block (an Image block, or a Media block with a picture or
 * a video poster), or the background the artist uploaded.
 */
export function coverImageUrl(version: PublishedVersionFacts | null | undefined): string | null {
  const widgets = version?.widgets ?? [];
  const blockUrl = [...widgets]
    .sort((a, b) => a.position - b.position)
    .map(widgetPictureUrl)
    .find((url): url is string => url !== null && url !== '');
  return blockUrl ?? version?.backgroundFile?.url ?? null;
}

/** True when the page shows artwork of its own. */
export function hasCoverArt(version: PublishedVersionFacts | null | undefined): boolean {
  return coverImageUrl(version) !== null;
}

/**
 * Splits the address so the host can be greyed and the slug emphasised. The
 * scheme goes with the host: nobody reads it, and it must not compete with the
 * part the artist is about to paste somewhere.
 */
export function splitShareUrl(shareUrl: string): { host: string; slug: string } {
  const withoutScheme = shareUrl.replace(/^https?:\/\//, '');
  const slashIndex = withoutScheme.indexOf('/');
  if (slashIndex === -1) {
    return { host: withoutScheme, slug: '' };
  }
  return { host: `${withoutScheme.slice(0, slashIndex)}/`, slug: withoutScheme.slice(slashIndex + 1) };
}
